/**
 * LANE V2 — the wire shapes of MSA's session-keyed routes (`/api/sessions/**`).
 *
 * WHY THIS FILE IS HAND-TYPED (and not just re-exported from `../protocol/weave-protocol-v1.d.ts`):
 * contracts (pinned in `apps/shell-web/scripts/codegen-weave-protocol.mjs`) now publish every one of these shapes,
 * but the generated interfaces are CLOSED where the server is newer than the client: enums are closed unions and
 * fields the schema marks optional are `undefined`-able. This module deliberately WIDENS closed enums to
 * `X | string` and keeps the nullability readers already narrow on, so the app tolerates unknown keys and
 * unknown enum values. `generatedTypesCompat.test.ts` is the compile-time drift check between the two
 * (generated -> hand); a field the contract adds and this file lacks fails `pnpm run type-check` there.
 * Import named generated types only (never numbered aliases such as `Status1` / `TaskId3`: they renumber on regen).
 * Every field the server may omit is optional.
 *
 * Mirrors MSA `app/lane_v2/read_routes.py` (R0–R5) + `routes.py` / `task_routes.py` / `package_routes.py`.
 */
import type { Interaction, InteractionSubject, ItemFileUnchanged } from "../protocol";

// ── enums (open: always tolerate an unknown string) ──────────────────────────────────────────────

/** MSA `app/core/enums.py::TaskState`. */
export type TaskState =
  | "exploring"
  | "plan_proposed"
  | "contracting"
  | "building"
  | "amendment_proposed"
  | "needs_input"
  | "needs_budget"
  | "assembling"
  | "changeset_proposed"
  | "checking"
  | "validated"
  | "applying"
  | "applied"
  | "cancelled"
  | "failed"
  | "held";

/** MSA `HoldReason` (contracts 0.2.28): why a task is (or will be) `held`. Open on the wire. */
export type HoldReason = "budget" | "user" | "harness";

/** MSA `TaskControl` (contracts 0.2.28): the route-only controls R3 offers. Open on the wire. */
export type TaskControl = "hold" | "resume";

/**
 * R3 `ended` (contracts 0.2.28, `TaskEndedView`): why a task ENDED or PARKED, written once by the machine. `cause` is a
 * closed server code that is OPEN on the wire (`applied`, `apply_rejected`, `cancelled`, `declined`, `ask_expired_exhausted`,
 * `revert_org_drifted`, `unrecoverable`, `items_failed`, `superseded`; `budget` on legacy rows). A `held` task has NOT ended.
 */
export interface TaskEnded {
  state: TaskState | string;
  cause: string;
  fatal?: string | null;
  item_keys?: string[];
  items_total?: number;
  last_check_id?: string | null;
  epoch?: number;
}

export const TERMINAL_TASK_STATES: ReadonlySet<string> = new Set(["applied", "cancelled", "failed"]);

/** MSA `app/work/types.py::ItemStatus`. */
export type WorkItemStatus = "pending" | "leased" | "built" | "validated" | "failed" | "blocked" | "dropped";

/** MSA `app/work/types.py::BuildStatus`. */
export type BuildStatus = "active" | "parked" | "done" | "failed" | "cancelled" | "superseded";

export type AskStatus = "pending" | "answered" | "expired" | "withdrawn" | "superseded";

/** Option ids MSA pins for the asks Core raises (`app/core/asks.py`). Agent-raised clarify asks use their own ids. */
export const OPTION_APPROVE = "approve";
export const OPTION_DECLINE = "decline";
export const OPTION_CHANGES_REQUESTED = "changes_requested";
export const OPTION_ACK = "ack";

// ── R0 capabilities ──────────────────────────────────────────────────────────────────────────────

export interface LaneV2Capabilities {
  /** Since read model 7 (contracts 0.2.20): transient `turn_activity` frames on the stream. */
  live_frames?: boolean;
  read_model: number;
  lane_v2: boolean;
  package_deploy_mode: "off" | "check_only" | "apply" | string;
  native_kinds?: boolean;
  /**
   * MSA can continue a pre-task INFORM ask deterministically: `POST …/asks/{a}/respond` records the
   * answer, then `POST …/turns` with `answers_ask_id` continues it without the `bind_reply` LLM.
   * Absent/false on an older server — those asks stay on the `turn`-only route (`askRouting.ts`).
   */
  turn_answers_ask?: boolean;
  /** `settings.lane_v2_offline`, read live on every call — leg `v2-offline-switch`. Absent means false. */
  offline?: boolean;
  /** `GET /api/sessions` (R6) and `POST /{id}/close` exist — leg `v2-session-list`. */
  session_list?: boolean;
  /** `POST /sessions/turns` accepts `attachments` — leg `v2-composer-parity`. */
  turn_attachments?: boolean;
  /** `POST /sessions/turns` accepts `ide_state` — leg `v2-composer-parity`. */
  turn_ide_state?: boolean;
  /** `POST /api/sessions/{session_id}/tasks/{task_id}/save` exists — leg `v2-save-branch`. */
  branch_save?: boolean;
  /** Since contracts 0.2.28: this MSA serves the desktop overlay. */
  desktop_overlay?: boolean;
  /** Since contracts 0.2.28: the caller's read-only environment facts; null/absent on an older server. */
  environment?: EnvironmentView | null;
}

/** R0 `environment` (contracts 0.2.28). `connected_providers` is null when the read did not finish in R0's bound. */
export interface EnvironmentView {
  instance_kind: string;
  deploy_mode: string;
  connected_providers: string[] | null;
  data_scope: string;
  branch_save: boolean;
}

/** `TurnOut.usage` / R1 `usage_total` / R5 per-turn `usage` — the one usage shape, reused verbatim
 *  by three call sites (`READ_MODEL_SPEC.md` §6). */
export interface UsageTotals {
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
  tool_calls: number;
}

// ── R1 session ───────────────────────────────────────────────────────────────────────────────────

export interface TaskSummary {
  task_id: string;
  state: TaskState | string;
  created_at: string;
  updated_at: string;
  current_plan_revision_id?: string | null;
  current_package_hash?: string | null;
  /** Why a `held` task holds (contracts 0.2.28); null otherwise. */
  hold_reason?: HoldReason | string | null;
  /** Since contracts 0.2.32 (RMV 15): why the task rests in `validated`; null while it does not. */
  rest_verdict?: RestVerdictView | null;
}

/** `rest_verdict` (contracts 0.2.32 `RestVerdictView`): `code` is open on the wire (`passed`, `coverage_shortfall`, `recheck_failed`, `not_run`, `nothing_deployed`). */
export interface RestVerdictView {
  code: string;
  policy?: string | null;
}

export interface LaneV2Session {
  session_id: string;
  lane: "v2";
  tasks: TaskSummary[];
  open_ask_count: number;
  last_seq?: number | null;
  /** Since leg `v2-usage-wire`: always a dict of zeros or more, never null/absent on a real server. */
  usage_total?: UsageTotals;
}

// ── R6 session list ──────────────────────────────────────────────────────────────────────────────

export interface SessionListingTasks {
  count: number;
  open_count: number;
  latest_state: string | null;
}

/** `GET /api/sessions` row — leg `v2-session-list`. */
export interface SessionListing {
  session_id: string;
  lane: "v2";
  title: string | null;
  created_at: string;
  closed_at: string | null;
  last_turn_at: string | null;
  turn_count: number;
  last_outcome: string | null;
  tasks: SessionListingTasks;
  open_ask_count: number;
}

export interface SessionListOut {
  sessions: SessionListing[];
  read_model: number;
}

/** `POST /api/sessions/{id}/close` — R6's write half. */
export interface SessionCloseOut {
  session_id: string;
  closed_at: string;
}

/**
 * A session's live compaction (MSA leg `cv-compact`): Weave carries `summary` in place of every turn
 * whose `turn_seq` is at or below `turn_seq`. The turns themselves are untouched and still listed.
 */
export interface SessionCompaction {
  turn_seq: number;
  summary: string;
  focus?: string | null;
  created_at: string;
}

/** `GET /api/sessions/{id}/compaction`. A 404 here means this server has no compact routes. */
export interface SessionCompactionOut {
  session_id: string;
  compaction: SessionCompaction | null;
}

/** `POST /api/sessions/{id}/compact`. */
export interface SessionCompactOut {
  session_id: string;
  compaction: SessionCompaction;
  turns_covered: number;
}

// ── R2 asks ──────────────────────────────────────────────────────────────────────────────────────

/** `dialogue.resolved_payload(native_kinds=True)` — an `interaction.resolved` body. */
export interface AskResolution {
  interaction_id: string;
  kind: string;
  resolution: {
    option_id?: string;
    free_text?: string;
    selected?: string[];
    ref_id?: string;
    subject?: InteractionSubject;
    approved?: boolean;
  };
  resolved_by?: string;
  resolved_at?: string;
  applied_to?: unknown;
}

/**
 * MSA leg `cv-connector-writes`: what an approved write to a connected tool (Notion, Jira, …) did. The same shape arrives
 * twice — as the respond payload's `applied_to` the instant the click returns, and as the `connector_write_done` journal
 * frame on the session stream (so every open view, not only the clicking one, learns it). `error` is the provider's own
 * refusal as one plain line (MSA `provider_error_text`); `url` / `title` are the provider's canonical record on success.
 */
export interface ConnectorWriteOutcome {
  ask_id: string;
  provider: string;
  action: string;
  ok: boolean;
  url: string | null;
  title: string | null;
  error: string | null;
}

export interface AskView {
  task_id: string | null;
  created_at: string;
  status: AskStatus | string;
  /** Contracts 0.2.15 `Interaction`, NATIVE kinds (`approve_plan_revision`, never the `approve_plan` alias). */
  interaction: Interaction;
  resolution?: AskResolution | null;
}

// ── R3 task snapshot ─────────────────────────────────────────────────────────────────────────────

export interface TransitionView {
  epoch: number;
  from: string | null;
  to: string;
  event: string;
  at: string;
  reason?: string | null;
}

export interface PlanItemView {
  item_id: string;
  metadata_type: string;
  full_name: string;
  op: "create" | "modify" | "delete" | string;
  rationale?: string | null;
  depends_on?: string[];
  irreversible?: boolean;
  phase?: string | null;
  manual_prerequisites?: string[];
  /** Since contracts 0.2.30 (RMV 13): the detail a reviewer reads before approving. All optional, all bounded server-side (200 / 20). */
  references?: PlanItemReferenceView[];
  change_sites?: string[];
  verification_gaps?: string[];
  field_values?: FieldValueView[];
  value_set_pairs?: FieldValueView[];
  model_supplied_input?: ModelSuppliedInputView[];
  derived_from?: DerivedFromView | null;
  primary?: boolean | null;
  provenance?: string | null;
}

/** A component a plan item references (contracts 0.2.30). */
export interface PlanItemReferenceView {
  metadata_type: string;
  full_name: string;
}

/** One `field = value` pair of a plan item (contracts 0.2.30). */
export interface FieldValueView {
  field: string;
  value: string;
  /** Since contracts 0.2.30: `needs_value` marks a pair the person still has to supply; open on the wire. */
  status?: "needs_value" | null | string;
  label?: string | null;
}

/** An input the model supplied rather than the user (contracts 0.2.30). */
export interface ModelSuppliedInputView {
  input_key: string;
  value: string;
  basis?: string | null;
  human?: boolean | null;
}

/** The exemplar a plan item was derived from (contracts 0.2.30); `kind` is an open server code. */
export interface DerivedFromView {
  exemplar_full_name: string;
  kind?: string | null;
}

export interface OutOfScopeView {
  what: string;
  metadata_type?: string | null;
  full_name?: string | null;
  /** `unsupported_type | modify_of_create_only_type | manual_prerequisite | needs_human` (open). */
  reason: string;
}

/** `plan_revisions.coverage` as stored (leg plan-coverage-guard). Everything optional: older revisions have none. */
export interface CoverageRecord {
  result?: "checked" | "unchecked" | string;
  model?: string | null;
  error?: string | null;
  units?: Array<{
    unit: string;
    verdict: "planned" | "out_of_scope" | "missing" | string;
    reason?: string | null;
    covered_by?: string | null;
    metadata_type?: string | null;
    full_name?: string | null;
  }>;
  repair?: string;
  initial_gaps?: number;
  system_disclosed?: number;
  context_incomplete?: boolean;
  repair_added_items?: number;
}

/** One E1 org-model finding (`orgmodel_api.ComponentFinding.as_dict()`), mirrored verbatim — opaque
 *  beyond `kind`, which every finding carries and `PlanRevisionView` groups by. */
export interface OrgFindingView {
  kind: string;
  [k: string]: unknown;
}

export interface PlanView {
  plan_revision_id: string;
  plan_id: string;
  version: number;
  parent_id?: string | null;
  author?: string | null;
  origin?: string | null;
  content_sha256: string;
  created_at?: string | null;
  goal?: string | null;
  items: PlanItemView[];
  assumptions?: string[];
  open_questions?: string[];
  out_of_scope?: OutOfScopeView[];
  coverage?: CoverageRecord | null;
  /** Since leg `v2-plan-org-findings`: `[]`/`0` both when E1 found nothing AND when it never ran —
   *  `org_findings_checked` is the only thing that disambiguates the two. */
  org_findings?: OrgFindingView[];
  org_findings_total?: number;
  org_findings_checked?: boolean;
}

export interface PlanRevisionRef {
  plan_revision_id: string;
  version: number;
  parent_id?: string | null;
  author?: string | null;
  origin?: string | null;
  created_at?: string;
  content_sha256: string;
}

export interface WorkItemView {
  work_item_id: string;
  item_id?: string | null;
  component_key?: string | null;
  metadata_type: string;
  full_name: string;
  op: string;
  /** `component | test` */
  kind?: string;
  status: WorkItemStatus | string;
  attempt?: number;
  max_attempts?: number;
  level?: number | null;
  depends_on?: string[];
  error_code?: string | null;
  verdict?: unknown;
  manual_prerequisites?: string[];
  has_body?: boolean;
  updated_at?: string;
  repair_of?: string | null;
  /** Since contracts 0.2.32 (RMV 15): the latest attempt's verdict per obligation. */
  obligations?: BuildItemObligationView[];
  obligations_unmet?: number;
}

/** One obligation verdict (contracts 0.2.32). `status`: `met | already_met | changed_unverified | unmet | unjudged` (open); `code` is the open failure code. */
export interface BuildItemObligationView {
  id: string;
  status: string;
  code?: string | null;
  evidence?: ObligationEvidenceView | null;
}

/** Evidence levels reached for one obligation (contracts 0.2.32); `null`/absent = not measured. */
export interface ObligationEvidenceView {
  E0?: boolean | string | null;
  E1?: boolean | string | null;
  E2?: boolean | string | null;
  E2_fraction?: number | null;
  E3?: boolean | string | null;
}

export interface BuildView {
  build_id: string;
  status: BuildStatus | string;
  park_reason?: string | null;
  counts?: Record<string, number>;
  items: WorkItemView[];
  predecessors?: string[];
}

export interface PackageMemberView {
  metadata_type: string;
  full_name: string;
  op: string;
  body_sha256?: string | null;
  baseline_sha?: string | null;
  /** `validated` | `not_validated` | `validation_failed` (open enum; the label is never a gate). */
  validation_status?: string | null;
  /** Why the member is not `validated` — mostly sentences, a few machine codes (`validationReasonLabel` in views.ts). */
  validation_reasons?: string[] | null;
  /** Who said the label and why; `reason === "held_back"` marks a member held back from the changeset (`isHeldBack`). */
  validation_fact?: { reason?: string | null } | null;
  [k: string]: unknown;
}

export interface ExcludedComponentView {
  metadata_type: string;
  full_name: string;
  reason: string;
  [k: string]: unknown;
}

/** One `branch_saved` journal row, as R3's package view lists it — newest first, up to 20. */
export interface BranchSaveRecord {
  version: number;
  branch: string;
  files_count: number;
  at: string;
}

export interface PackageView {
  package_id: string;
  package_hash: string;
  members: PackageMemberView[];
  excluded: ExcludedComponentView[];
  manual_prerequisites?: string[];
  /** Since leg `v2-save-branch`: `[]` when never saved, never omitted/null. Only on THIS (R3) view,
   *  never on the standalone `GET .../package/{id}` route (`READ_MODEL_SPEC.md` §7). */
  branch_saves?: BranchSaveRecord[];
  /** Since contracts 0.2.32 (RMV 15): does the changeset do what was asked; null until judged. */
  acceptance?: AcceptanceView | null;
  /**
   * An async branch save in flight, derived server-side from the journal; null/absent otherwise. R3 only.
   */
  branch_save_running?: BranchSaveRunningView | null;
}

/**
 * `PackageView.branch_save_running` (contracts 0.2.37).
 */
export interface BranchSaveRunningView {
  save_id: string;
  mode: "async" | "sync" | string;
  branch: string | null;
  started_at: string;
  chunks_written: number;
}

/** `PackageView.acceptance` (contracts 0.2.32). `verdict`: `does_request | partial | does_not | not_judged`; `basis`: `facts | facts_and_judge` (both open). `unmet` is bounded at 50, `unmet_total` is the full count. */
export interface AcceptanceView {
  verdict: string;
  basis: string;
  facts: AcceptanceFactsView;
  unmet?: AcceptanceUnmetView[];
  unmet_total?: number;
}

export interface AcceptanceFactsView {
  elements_added?: number;
  elements_removed?: number;
  elements_changed?: number;
  obligations_total?: number;
  obligations_unmet?: number;
  obligations_unverified?: number;
  obligations_unjudged?: number;
  primary_failed?: number;
}

export interface AcceptanceUnmetView {
  component_key: string;
  obligation_id?: string | null;
  kind?: string | null;
  code: string;
}

export interface CheckView {
  deploy_id?: string;
  /** `null` when the org has never reported a result for this deploy_id at all (contracts 0.2.16
   *  confirmed this on the wire, not just "false" -- distinct from a completed check that FAILED).
   *  Render `null` as an "unknown"/"not run" state, never as a failure. */
  ok: boolean | null;
  detail?: string | null;
  /** component_key → messages (as the `check_finished` frame carries it). */
  failures?: Record<string, string[] | string> | null;
  coverage?: Record<string, unknown> | null;
  completed_date?: string | null;
  test_level?: string | null;
  run_tests?: string[] | null;
  /** Since contracts 0.2.30 (RMV 13). */
  notes?: string[];
  /** `sweep` = the task's own check after the build, `revalidate` = a person's Validate click (open enum). */
  origin?: "sweep" | "revalidate" | string | null;
  test_scope?: string | null;
  created_at?: string | null;
  finished_at?: string | null;
}

/** `TaskSnapshot.validation`: the latest check plus (since 0.2.30) up to 20 checks, the history. */
export interface ValidationData {
  validation_id: string | null;
  last_check: CheckView | null;
  checks?: CheckView[];
}

/** What a task at rest offers (contracts 0.2.30; `request_changes` / `discard` since 0.2.31). Server-driven: the GUI renders exactly these, in this order, and no others. Open on the wire. */
export type TaskAction = "save" | "validate" | "request_changes" | "discard" | (string & {});

export interface DeploymentView {
  deploy_id: string;
  kind?: string;
  status?: string;
  created_at?: string;
  [k: string]: unknown;
}

export interface TaskSnapshot {
  task_id: string;
  session_id: string;
  state: TaskState | string;
  epoch: number;
  created_at: string;
  updated_at: string;
  cancel_requested?: boolean;
  transitions: TransitionView[];
  plan: PlanView | null;
  plan_revisions?: PlanRevisionRef[];
  build: BuildView | null;
  package: PackageView | null;
  validation: ValidationData | null;
  deployments?: DeploymentView[];
  /** Since contracts 0.2.30: `[]` (or absent on an older server) while nothing is offered. */
  actions?: TaskAction[];
  /** Why the task is held (`budget` | `user` | `harness`), or null (contracts 0.2.28). */
  hold_reason?: HoldReason | string | null;
  /** A hold requested but not yet taken (the current component finishes first). */
  hold_pending?: HoldReason | string | null;
  /** Route-only controls on offer (`hold` / `resume`); the story strip builds Pause / Resume from this. */
  task_controls?: Array<TaskControl | string>;
  /** Why the task ended or parked (the standing `TaskEnded` record), or null. */
  ended?: TaskEnded | null;
  /** Since contracts 0.2.32 (RMV 15): why the task rests in `validated`; null while it does not. */
  rest_verdict?: RestVerdictView | null;
}

/** `POST .../tasks/{t}/validate` body (contracts 0.2.30 `ValidateIn`): the same key replays, never a second check. */
export interface ValidateIn {
  idempotency_key: string;
}

/** 202 of the validate route (contracts 0.2.30 `ValidateOut`). */
export interface ValidateOut {
  task_id: string;
  state: TaskState | string;
  status: "recorded" | "replayed" | string;
  deploy_id?: string | null;
}

/** `POST .../tasks/{t}/request-changes` body (contracts 0.2.31 `RequestChangesIn`): `text` is 1..4000 chars; the same key replays. */
export interface RequestChangesIn {
  text: string;
  idempotency_key: string;
}

/** 202 of request-changes (contracts 0.2.31 `RequestChangesOut`). */
export interface RequestChangesOut {
  task_id: string;
  state: TaskState | string;
  status: "recorded" | "replayed" | string;
}

/** `POST .../tasks/{t}/discard` body (contracts 0.2.31 `DiscardIn`). */
export interface DiscardIn {
  idempotency_key: string;
}

/** 200 of discard (contracts 0.2.31 `DiscardOut`); `ended_cause` is the `TaskEnded.cause` (`declined`). */
export interface DiscardOut {
  task_id: string;
  state: TaskState | string;
  status: "recorded" | "replayed" | string;
  ended_cause: string;
}

// ── R4 item body ─────────────────────────────────────────────────────────────────────────────────

export interface ItemBody {
  work_item_id: string;
  metadata_type: string;
  full_name: string;
  op: string;
  files: Array<{
    path: string;
    body: string | null;
    baseline: string | null;
    /** R4 wire `unchanged` — the generated contract type (0.2.22); the one owner. */
    unchanged?: ItemFileUnchanged;
  }>;
  baseline_known: boolean;
  truncated: boolean;
}

// ── composer parity (attachments + IDE state) ───────────────────────────────────────────────────────

export type AttachmentKind = "image" | "pdf" | "audio" | "url" | "text" | "code";

/**
 * `POST /sessions/turns` request-side attachment (`app.preprocess.service.AttachmentIn`). This client
 * always sends PRE-RESOLVED attachments (`markdown` filled by the existing `/api/runs/preprocess`
 * pipeline `useAttachments` already runs) rather than raw `content`/`data_base64` — MSA accepts
 * either, resolving raw payloads itself, but there is no reason to duplicate that resolution here.
 */
export interface AttachmentIn {
  kind: AttachmentKind;
  name?: string;
  mime?: string | null;
  content?: string | null;
  data_base64?: string | null;
  language?: string | null;
  markdown?: string | null;
}

/** `app.lane_v2.intake.IdeStateIn` — a lane-v2-owned mirror of the legacy `IdeStateIn`, minus the
 *  "apply to workspace" fields v2 has no equivalent of. */
export interface IdeStateIn {
  open_file?: string | null;
  workspace_files?: string[] | null;
  branch_names?: string[] | null;
  git_repo?: string | null;
  git_branch?: string | null;
}

/** R5 `turns[].attachments[]` — exactly these three keys, `kind` derived server-side from the stored
 *  intake artifact's type (`code` reads back as `"text"` — a cosmetic limitation, see the spec). */
export interface TurnAttachmentView {
  attachment_id: string;
  name: string;
  kind: string;
}

// ── R5 turns ─────────────────────────────────────────────────────────────────────────────────────

export interface TurnView {
  turn_id: string;
  turn_seq?: number | null;
  created_at: string;
  status: string;
  outcome?: string | null;
  user_text?: string | null;
  markdown?: string | null;
  task_id?: string | null;
  error_code?: string | null;
  /** Since leg `v2-usage-wire`: null (never zeros) when the turn spent nothing. */
  usage?: UsageTotals | null;
  /** Since leg `v2-composer-parity`: `[]` when the turn stored none. */
  attachments?: TurnAttachmentView[];
  /** Since leg `v2-composer-parity`: the compacted `IdeStateIn` this turn was submitted with. */
  ide_state?: IdeStateIn | null;
  /** Since contracts 0.2.28 (RMV 11): what the turn's tool calls amounted to. Leg `gui-what-i-read` reads `tool_calls` only. */
  trace?: TurnTraceView | null;
}

/** The subset of contracts `TurnTraceView` the GUI reads; the generated `TurnTraceView` (pin is past 0.2.28) is wider; this stays the subset the GUI reads. */
export interface TurnTraceView {
  tools?: string[];
  tool_calls?: number;
  components_read?: number;
}

// ── existing write routes ────────────────────────────────────────────────────────────────────────

export interface TicketOut {
  session_id: string;
  ticket: string;
  stream_url: string;
}

/** `POST /api/sessions/turns` result (mirrors Core's DispatchResult). The request BLOCKS for the whole turn. */
export interface TurnOut {
  session_id: string;
  turn_id: string;
  lane: string;
  outcome: string;
  task_id?: string | null;
  task_state?: string | null;
  markdown?: string | null;
  ask_ids: string[];
  error_code?: string | null;
  detail?: Record<string, unknown>;
  stream_url: string;
  sse_ticket: string;
  /** Since leg `v2-usage-wire`: null (best-effort read-back) when the turn spent nothing. */
  usage?: UsageTotals | null;
}

// ── save to branch (leg `v2-save-branch`) ───────────────────────────────────────────────────────────

/** `POST /api/sessions/{s}/tasks/{t}/save` request body (`SaveBranchIn`, `extra="forbid"`). */
export interface SaveBranchIn {
  branch: string;
  message?: string | null;
  component_keys?: string[] | null;
  package_hash?: string | null;
}

/**
 * One `files[]` row of the save response — sent or skipped (contracts `SaveFileEntry`).
 * MSA always sends these six keys on written and skipped rows; required deliberately against the optional contract fields.
 */
export interface SaveBranchFileResult {
  component_key: string | null;
  path: string;
  status: string;
  validation_status: string | null;
  result: string | null;
  conflict_checked: boolean | null;
  /** MSA's skip detail (e.g. `body_cap:<n>`, `body_cap_alone:<n>`, `peer_unavailable`, `replay_refused`, `peer_rejected:<code>`, `merge_refused:<code>`), sent only on skipped rows; parse by prefix token only. */
  detail?: string | null;
  /** Only on rows written by a full-merge save (Profile/CustomLabels), from the peer's flag; informational, never a block; absent otherwise. */
  has_conflicts?: boolean | null;
}

export interface SaveBranchOut {
  task_id: string;
  package_hash: string;
  branch: string;
  version: number;
  revision: unknown;
  replayed: boolean;
  stale_package: boolean;
  files: SaveBranchFileResult[];
}

/** The verbatim peer conflict description carried by `409 BRANCH_CONFLICT` (see `branchConflictOf`). */
export interface BranchConflictBody {
  error_code: "BRANCH_CONFLICT";
  revision: unknown;
  files: unknown[];
  /** Since contracts 0.2.35: the files a chunked save had already written before the conflict. */
  written?: SaveBranchFileResult[] | null;
}

/** The peer's own machine code carried by `422 BRANCH_SAVE_REJECTED` (see `branchSaveRejectionOf`). */
export interface BranchSaveRejectedBody {
  error_code: "BRANCH_SAVE_REJECTED";
  peer_code: string | null;
}

/** `202` of the save route when it runs in the background (contracts 0.2.35 `BranchSaveAccepted`). */
export interface BranchSaveAccepted {
  save_id: string;
  task_id: string;
  state: "running" | string;
}

/** `GET .../tasks/{t}/saves/{save_id}` (contracts 0.2.35 `BranchSavePoll`). `state`: `running | done | failed | interrupted` (open). */
export interface BranchSavePoll {
  save_id: string;
  task_id: string;
  state: "running" | "done" | "failed" | "interrupted" | string;
  chunks_written: number;
  http_status?: number | null;
  result?: Record<string, unknown> | null;
}

/**
 * `branch_save_job` session frame payload (contracts 0.2.37): ids and state only.
 */
export interface BranchSaveJobFramePayload {
  save_id: string;
  task_id: string;
  state: string;
  branch?: string | null;
  mode?: "async" | "sync" | string | null;
}

export interface RevertOut {
  task_id: string;
  inverse_package_hash: string;
  ask_id: string;
  unsupported?: unknown;
  residue?: string[];
}

/** `POST .../tasks/{t}/hold` and `.../resume` (contracts `HoldOut`): always 200, the typed `status` says what happened. */
export interface TaskHoldOut {
  session_id: string;
  task_id: string;
  task_state: string;
  /** hold: `requested` | `already_pending` | `not_holdable`; resume: `resumed` | `not_held`. Open on the wire. */
  status: "requested" | "already_pending" | "not_holdable" | "resumed" | "not_held" | string;
  hold_reason?: HoldReason | string | null;
}

export interface TaskCancelOut {
  session_id: string;
  task_id: string;
  task_state: string;
  cancelled: boolean;
  reason?: "apply_in_progress" | "task_not_cancellable" | string | null;
}

// ── session stream ───────────────────────────────────────────────────────────────────────────────

/** Contracts 0.2.15 `SessionFrame` (`type` is an OPEN string: ignore unknown ones). */
export interface SessionFrameWire {
  seq: number;
  type: string;
  payload: Record<string, unknown>;
  task_id: string | null;
  run_id: string | null;
  at: string;
}
